import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL, publicApiHeaders } from '../config/apiConfig';

const quickReplies = [
  'Quiero conocer los servicios',
  'Necesito ayuda con lactancia',
  'Quiero organizar la llegada del bebé'
];

function createSessionId() {
  if (window.crypto?.randomUUID) {
    return `chat_${window.crypto.randomUUID()}`;
  }

  return `chat_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function normalizeMessages(messages) {
  return messages.map((message) => ({
    ...message,
    sender: message.sender === 'admin' ? 'bot admin' : message.sender,
    text: message.sender === 'admin' && message.displayName
      ? message.displayName + ': ' + message.text
      : message.text
  }));
}

function renderMessageText(text) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);

  return parts.map((part, index) => {
    if (!part.match(/^https?:\/\//)) {
      return part;
    }

    const href = part.replace(/[.,;!?]+$/, '');
    const suffix = part.slice(href.length);
    const label = href.includes('#contacto') ? 'Ir al formulario de contacto' : href;

    return (
      <React.Fragment key={`message-link-${index}`}>
        <a href={href}>{label}</a>{suffix}
      </React.Fragment>
    );
  });
}

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [adminActive, setAdminActive] = useState(false);
  const [isMessagingHealthy, setIsMessagingHealthy] = useState(false);
  const [sessionId] = useState(() => {
    const savedSessionId = window.localStorage.getItem('babyplanner_chat_session');
    const nextSessionId = savedSessionId || createSessionId();
    window.localStorage.setItem('babyplanner_chat_session', nextSessionId);
    return nextSessionId;
  });
  const [messages, setMessages] = useState([
    {
      id: 'initial_message',
      sender: 'bot',
      text: '¡Hola! Soy el asistente de Babyplanner Vero. Puedo ayudarte a encontrar el servicio ideal o resolver dudas iniciales.'
    }
  ]);
  const messagesRef = useRef(null);

  const unreadLabel = useMemo(() => (isOpen ? 'Cerrar chat' : 'Abrir chat'), [isOpen]);

  const syncSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/${sessionId}`, {
        headers: publicApiHeaders()
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (data.messages?.length) {
        setMessages(normalizeMessages(data.messages));
      }
      setAdminActive(Boolean(data.adminActive));
    } catch (error) {
      // El backend del chat puede estar apagado durante la vista local.
    }
  };

  useEffect(() => {
    syncSession();
    const interval = window.setInterval(syncSession, 3000);

    return () => window.clearInterval(interval);
  }, [sessionId]);

  useEffect(() => {
    let active = true;

    const checkMessagingHealth = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/health`);
        if (!response.ok) throw new Error('Health no disponible');
        const data = await response.json();
        const providerAvailable = Boolean(
          data.activeChatProvider
          || data.lmStudio?.available
          || data.ollama?.available
        );
        if (active) setIsMessagingHealthy(providerAvailable);
      } catch (error) {
        if (active) setIsMessagingHealthy(false);
      }
    };

    checkMessagingHealth();
    const interval = window.setInterval(checkMessagingHealth, 30000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();

    if (!trimmed || isLoading) {
      return;
    }

    const optimisticUserMessage = {
      id: `pending_${Date.now()}`,
      sender: 'user',
      text: trimmed
    };

    setMessages((currentMessages) => [
      ...currentMessages,
      optimisticUserMessage
    ]);
    setInput('');
    setIsOpen(true);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: publicApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          sessionId,
          message: trimmed
        })
      });

      const data = await response.json();

      if (!response.ok && !data.messages) {
        throw new Error(data.error || 'No se pudo enviar el mensaje.');
      }

      setMessages(normalizeMessages(data.messages || []));
      setAdminActive(Boolean(data.adminActive));
    } catch (error) {
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `error_${Date.now()}`,
          sender: 'bot error',
          text: 'No pude conectar con el servidor del chat. Inténtalo nuevamente en unos minutos.'
        }
      ]);
      // El backend del chat puede estar apagado durante la vista local.
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  return (
    <div className={`chat-widget ${isOpen ? 'is-open' : ''}`}>
      {isOpen && (
        <section className="chat-panel" aria-label="Chat de Babyplanner Vero">
          <header className="chat-header">
            <div>
              <span className="chat-status"></span>
              <strong>Babyplanner Vero</strong>
              <p>{adminActive ? 'Te responde una persona' : 'Asistente con LM Studio'}</p>
            </div>
            <button type="button" className="chat-close" onClick={() => setIsOpen(false)} aria-label="Cerrar chat">
              ×
            </button>
          </header>

          <div className="chat-messages" aria-live="polite" ref={messagesRef}>
            {messages.map((message) => (
              <div className={`chat-message ${message.sender}`} key={message.id}>
                {renderMessageText(message.text)}
              </div>
            ))}
            {isLoading && (
              <div className="chat-message bot typing">
                {adminActive ? 'Enviando' : 'Escribiendo'}<span>.</span><span>.</span><span>.</span>
              </div>
            )}
          </div>

          <div className="chat-quick-replies" aria-label="Respuestas rápidas">
            {quickReplies.map((reply) => (
              <button type="button" key={reply} onClick={() => sendMessage(reply)} disabled={isLoading}>
                {reply}
              </button>
            ))}
          </div>

          <form className="chat-form" onSubmit={handleSubmit}>
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Escribe tu consulta..."
              aria-label="Mensaje para el chat"
              disabled={isLoading}
            />
            <button type="submit" disabled={isLoading || !input.trim()}>
              {isLoading ? '...' : 'Enviar'}
            </button>
          </form>
        </section>
      )}

      <button
        type="button"
        className="chat-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={unreadLabel}
        aria-expanded={isOpen}
      >
        <span className="chat-toggle-icon">{isOpen ? '×' : 'Chat'}</span>
        {!isOpen && (
          <span
            className={`chat-notification ${isMessagingHealthy ? 'is-healthy' : ''}`}
            title={isMessagingHealthy ? 'Mensajería disponible' : 'Mensajería no disponible'}
          >
            1
          </span>
        )}
      </button>
    </div>
  );
}

export default ChatWidget;
